const pad = (n: number) => String(n).padStart(2, '0')

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const diff = Date.now() - then
  const min = Math.floor(diff / 60000)
  if (min < 1) return '刚刚'
  if (min < 60) return `${min} 分钟前`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour} 小时前`
  const day = Math.floor(hour / 24)
  if (day < 30) return `${day} 天前`
  return formatDate(iso)
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

export type ExpiryState = 'permanent' | 'active' | 'expiring' | 'expired' | 'none'

export interface ExpiryInfo {
  state: ExpiryState
  label: string
}

const DAY = 86400000

/** 7 天内为即将到期。 */
export function lineState(status: string | null | undefined, expiresAt: string | null | undefined): ExpiryInfo {
  switch (status) {
    case 'active':
      return expiryInfo(expiresAt, true)
    case 'expired':
      return { state: 'expired', label: '已过期' }
    case 'pending':
      return { state: 'expiring', label: '开通中' }
    case 'disabled':
      return { state: 'none', label: '已停用' }
    default:
      return { state: 'none', label: '未开通' }
  }
}

export function expiryInfo(expiresAt: string | null | undefined, active: boolean): ExpiryInfo {
  if (!active) return { state: 'none', label: '未激活' }
  if (!expiresAt) return { state: 'permanent', label: '永久' }
  const t = new Date(expiresAt).getTime()
  if (Number.isNaN(t)) return { state: 'none', label: '—' }
  const left = t - Date.now()
  if (left <= 0) return { state: 'expired', label: '已过期' }
  if (left <= 7 * DAY) return { state: 'expiring', label: `${formatDate(expiresAt)} 到期` }
  return { state: 'active', label: `${formatDate(expiresAt)} 到期` }
}

const LOCALE_NAMES: Record<string, string> = {
  en: 'English',
  zh: '中文',
  'zh-tw': '繁體中文',
  ja: '日本語',
  ko: '한국어',
  es: 'Español',
  pt: 'Português',
  'pt-br': 'Português (BR)',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  ru: 'Русский',
  ar: 'العربية',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  tr: 'Türkçe',
  hi: 'हिन्दी',
}

export function localeName(code: string): string {
  return LOCALE_NAMES[code.toLowerCase()] ?? code
}

const ACTION_NAMES: Record<string, string> = {
  'admin.login': '登录后台',
  'admin.logout': '登出后台',
  'line.grant': '开通/续期线路',
  'line.disable': '停用线路',
  'entitlement.grant': '激活/续期权益',
  'entitlement.revoke': '撤销权益',
  'catalog.sync': '同步目录',
}

const TARGET_NAMES: Record<string, string> = {
  user: '账号',
  line: '线路',
  catalog: '目录',
}

export function actionName(action: string): string {
  return ACTION_NAMES[action] ?? action
}

export function targetLabel(type: string, id: string): string {
  const name = TARGET_NAMES[type] ?? type
  if (!id) return name
  return `${name} ${shortID(id)}`
}

/** ISO → datetime-local（本地时间） */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** datetime-local → ISO */
export function fromLocalInput(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

export function shortID(id: string, head = 8, tail = 4): string {
  if (!id) return ''
  if (id.length <= head + tail + 1) return id
  return `${id.slice(0, head)}…${id.slice(-tail)}`
}
